import { useState } from "react";
import { useNavigate } from "react-router-dom";

const modeData = [
  {
    key: "A" as const,
    badge: "MODE A",
    title: "제로 투 원",
    subtitle: "Zero to One · Start-up",
    desc: "바닥에서 시작하는 완전한 창업 시뮬레이션. 사업자등록부터 IR 피칭까지 창업의 험난한 과정을 그대로 경험합니다.",
    features: ["가상 사업자등록 + 법인 설립", "IR 피칭으로 초기 자본 확보", "회사 이름 · 로고 직접 설계", "초반 AI 거래처 신뢰 장벽 극복", "16주 장기 프로젝트 최적화"],
    capital: "0원 (IR 피칭으로 확보)",
    duration: "한 학기 (16주)",
    difficulty: 4,
  },
  {
    key: "B" as const,
    badge: "MODE B",
    title: "플러그 앤 플레이",
    subtitle: "Plug & Play · Onboarding",
    desc: "이미 운영 중인 회사에 신입으로 입사. 로그인 즉시 쏟아지는 AI 메일로 실무 사이클에 바로 탑승합니다.",
    features: ["자본금 5,000만원 + 소속 아티스트 1팀", "기본 거래처 관계 이미 구축", "로그인 즉시 AI 메일 3통 대기", "특정 직무 집중 체험 가능", "1~2일 해커톤 · 워크샵 최적화"],
    capital: "5,000만원 기본 지급",
    duration: "1~2일 (워크샵)",
    difficulty: 2,
  },
];

export default function ModeSelect() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<"A" | "B" | null>(null);

  const handleStart = () => {
    if (!selected) return;
    localStorage.setItem('kap_play_mode', selected);
    navigate("/company-select");
  };

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex flex-col items-center justify-center px-8 py-12">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <img src="https://storage.readdy-site.link/project_files/e81f0f74-7f28-46c8-8485-3df778381a50/9a17726c-ea2b-4ef1-b154-44e1a8dd8821_Gemini_Generated_Image_at6qf4at6qf4at6q-removebg-preview.png?v=7e9fc5a9989fd2f2c3e90b758ae037ba" alt="K-ArtPEN" className="w-8 h-8 rounded-lg object-contain opacity-70" />
          <span className="text-[#F2F2F2] font-semibold text-sm tracking-wide">K-ArtPEN</span>
        </div>
        <h1 className="text-[#F2F2F2] text-2xl font-semibold tracking-tight mb-3">시뮬레이션 모드를 선택하세요</h1>
        <p className="text-[#555555] text-sm max-w-md mx-auto leading-relaxed">교육 목표와 수업 기간에 맞는 모드를 선택하면 최적화된 가상 엔터테인먼트 환경이 시작됩니다.</p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 gap-4 w-full max-w-2xl mb-8">
        {modeData.map(mode => {
          const isSelected = selected === mode.key;
          return (
            <button
              key={mode.key}
              type="button"
              onClick={() => setSelected(mode.key)}
              className={`relative text-left rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden p-0 ${
                isSelected ? 'border-[#4B6CF7]/40 bg-[#4B6CF7]/[0.06]' : 'border-white/[0.06] bg-[#111112] hover:border-white/10 hover:bg-white/[0.02]'
              }`}
            >
              <div className="p-6">
                {/* Badge */}
                <div className="flex items-center gap-2 mb-5">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border tracking-widest ${isSelected ? 'text-[#7A9BFF] border-[#4B6CF7]/30 bg-[#4B6CF7]/10' : 'text-[#3D3D3D] border-white/[0.05]'}`}>
                    {mode.badge}
                  </span>
                </div>

                <h2 className={`text-base font-semibold mb-1 transition-colors ${isSelected ? 'text-[#F2F2F2]' : 'text-[#666666]'}`}>{mode.title}</h2>
                <p className={`text-[10px] uppercase tracking-widest mb-4 ${isSelected ? 'text-[#444444]' : 'text-[#2A2A2A]'}`}>{mode.subtitle}</p>

                <p className={`text-xs leading-relaxed mb-5 ${isSelected ? 'text-[#777777]' : 'text-[#333333]'}`}>{mode.desc}</p>

                <div className="space-y-1.5 mb-5">
                  {mode.features.map(f => (
                    <div key={f} className="flex items-start gap-2">
                      <div className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${isSelected ? 'bg-white/30' : 'bg-white/10'}`} />
                      <span className={`text-[11px] leading-relaxed ${isSelected ? 'text-[#555555]' : 'text-[#2A2A2A]'}`}>{f}</span>
                    </div>
                  ))}
                </div>

                <div className={`pt-4 border-t ${isSelected ? 'border-white/[0.08]' : 'border-white/[0.04]'} space-y-1.5`}>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#3D3D3D]">초기 자본금</span>
                    <span className={isSelected ? 'text-[#AAAAAA]' : 'text-[#333333]'}>{mode.capital}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#3D3D3D]">추천 기간</span>
                    <span className={isSelected ? 'text-[#AAAAAA]' : 'text-[#333333]'}>{mode.duration}</span>
                  </div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-[#3D3D3D]">난이도</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className={`w-3 h-0.5 rounded-full ${i <= mode.difficulty ? (isSelected ? 'bg-white/40' : 'bg-white/15') : 'bg-white/[0.05]'}`} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {isSelected && (
                <div className="absolute top-3.5 right-3.5 w-5 h-5 flex items-center justify-center rounded-full bg-white/10 border border-white/20">
                  <i className="ri-check-line text-white text-xs"></i>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={handleStart}
        disabled={!selected}
        className={`px-10 py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
          selected ? 'bg-[#4B6CF7] text-white hover:bg-[#5B7CF7] cursor-pointer' : 'bg-white/[0.04] text-[#2A2A2A] cursor-not-allowed'
        }`}
      >
        {selected ? `Mode ${selected} 시작 →` : '모드를 선택해주세요'}
      </button>

      <button type="button" onClick={() => navigate("/admin")}
        className="mt-4 text-[#2A2A2A] text-xs hover:text-[#555555] transition-colors cursor-pointer whitespace-nowrap">
        강사 / 관리자로 입장
      </button>
    </div>
  );
}
